/**
 * PROTOTYPE ONLY.
 *
 * Variant A — "Wizard". The doc as written: one question group per screen, a step
 * tracker in the right rail, a Check your answers screen before creation, then a
 * success screen offering the three optional customisations, then a task list for
 * editing. Two patterns, one for creating and one for managing.
 */
import {useState} from "react";
import {Alert, Button, Card, Divider, Space, Steps, Typography} from "antd";
import {
  Customisation,
  DraftSetter,
  ImportanceTag,
  SECTIONS,
  SectionFields,
  SectionKey,
  SessionDraft,
  customisationItems,
  sectionOneLiner,
  sectionSummary,
} from "./shared";

const {Title, Text, Paragraph} = Typography;

type Screen =
  | {name: "step"; index: number}
  | {name: "review"}
  | {name: "created"}
  | {name: "manage"}
  | {name: "editor"; section: SectionKey};

interface VariantProps {
  draft: SessionDraft;
  set: DraftSetter;
  customisation: Customisation;
  setCustomisation: (next: Customisation) => void;
}

export function VariantAWizard({
  draft,
  set,
  customisation,
  setCustomisation,
}: VariantProps) {
  const [screen, setScreen] = useState<Screen>({name: "step", index: 0});

  if (screen.name === "step") {
    const section = SECTIONS[screen.index];
    const isLast = screen.index === SECTIONS.length - 1;

    return (
      <div style={{display: "flex", gap: 24, alignItems: "flex-start"}}>
        <Card style={{flex: 1}}>
          <Title level={4} style={{marginTop: 0}}>
            {section.title}
          </Title>
          <Paragraph type="secondary">{section.blurb}</Paragraph>
          <Divider />
          <SectionFields section={section.key} draft={draft} set={set} />
          <Divider />
          <Space>
            {screen.index > 0 ? (
              <Button onClick={() => setScreen({name: "step", index: screen.index - 1})}>
                Back
              </Button>
            ) : null}
            <Button
              type="primary"
              onClick={() =>
                setScreen(
                  isLast ? {name: "review"} : {name: "step", index: screen.index + 1}
                )
              }
            >
              {isLast ? "Review answers" : "Continue"}
            </Button>
          </Space>
        </Card>

        <Card style={{width: 260}} bodyStyle={{padding: 16}}>
          <Steps
            direction="vertical"
            size="small"
            current={screen.index}
            items={SECTIONS.map((entry) => ({title: entry.title}))}
          />
        </Card>
      </div>
    );
  }

  if (screen.name === "review") {
    return (
      <Card>
        <Title level={4} style={{marginTop: 0}}>
          Check your answers
        </Title>
        <Paragraph type="secondary">
          Your session goes live as soon as you create it.
        </Paragraph>

        {SECTIONS.map((section, index) => (
          <div key={section.key} style={{marginBottom: 8}}>
            <Divider style={{margin: "12px 0"}} />
            <div style={{display: "flex", justifyContent: "space-between", gap: 16}}>
              <div>
                <Text strong>{section.title}</Text>
                {sectionSummary(section.key, draft).map((line) => (
                  <Paragraph key={line} style={{marginBottom: 0}} type="secondary">
                    {line}
                  </Paragraph>
                ))}
              </div>
              <Button type="link" onClick={() => setScreen({name: "step", index})}>
                Edit
              </Button>
            </div>
          </div>
        ))}

        <Divider />
        <Space>
          <Button onClick={() => setScreen({name: "step", index: SECTIONS.length - 1})}>
            Back
          </Button>
          <Button type="primary" onClick={() => setScreen({name: "created"})}>
            Create session
          </Button>
        </Space>
      </Card>
    );
  }

  if (screen.name === "created") {
    return (
      <Card>
        <Title level={3} style={{marginTop: 0}}>
          Your session is ready 🎉
        </Title>
        <Paragraph>
          <Text strong>{draft.name || "Your session"}</Text> has been created and can
          accept bookings right now. Customise it to give customers a better experience.
        </Paragraph>

        <Divider />
        <Title level={5}>Customise your session</Title>

        {customisationItems(customisation).map((item) => (
          <Card
            key={item.key}
            size="small"
            style={{marginBottom: 12}}
            title={
              <Space>
                {item.title}
                <ImportanceTag level={item.level} />
              </Space>
            }
            extra={
              <Button
                size="small"
                onClick={() =>
                  setCustomisation(applyCustomisation(customisation, item.key))
                }
              >
                {item.key === "coverImage" ? "Add cover image" : "Set up"}
              </Button>
            }
          >
            <Text type="secondary">{item.blurb}</Text>
            <br />
            <Text style={{fontSize: 12}}>{item.status}</Text>
          </Card>
        ))}

        <Divider />
        <Space>
          <Button type="primary" onClick={() => setScreen({name: "manage"})}>
            Done for now
          </Button>
          <Text type="secondary">(goes to the sessions table in the real product)</Text>
        </Space>
      </Card>
    );
  }

  if (screen.name === "editor") {
    const section = SECTIONS.find((entry) => entry.key === screen.section);

    return (
      <Card>
        <Button
          type="link"
          style={{paddingLeft: 0}}
          onClick={() => setScreen({name: "manage"})}
        >
          ← Back to session settings
        </Button>
        <Title level={4}>{section?.title}</Title>
        <SectionFields section={screen.section} draft={draft} set={set} />
        <Divider />
        <Space>
          <Button type="primary" onClick={() => setScreen({name: "manage"})}>
            Save
          </Button>
          <Button onClick={() => setScreen({name: "manage"})}>Cancel</Button>
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Title level={4} style={{marginTop: 0}}>
        Edit session
      </Title>
      <Paragraph type="secondary">
        Manage your session details and booking experience.
      </Paragraph>

      <Alert
        type="success"
        showIcon
        style={{marginBottom: 16}}
        message="Active"
        description="This session is live and accepting bookings."
      />

      <Title level={5}>Session</Title>
      {SECTIONS.map((section) => (
        <TaskRow
          key={section.key}
          title={section.title}
          status={sectionOneLiner(section.key, draft)}
          onClick={() => setScreen({name: "editor", section: section.key})}
        />
      ))}

      <Title level={5} style={{marginTop: 24}}>
        Booking experience
      </Title>
      {customisationItems(customisation).map((item) => (
        <TaskRow
          key={item.key}
          title={item.title}
          status={item.status}
          level={item.level}
          onClick={() => setCustomisation(applyCustomisation(customisation, item.key))}
        />
      ))}
    </Card>
  );
}

function TaskRow({
  title,
  status,
  level,
  onClick,
}: {
  title: string;
  status: string;
  level?: "required" | "recommended" | "optional";
  onClick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          onClick();
        }
      }}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        padding: "14px 4px",
        borderBottom: "1px solid #f0f0f0",
        cursor: "pointer",
      }}
    >
      <div>
        <Space>
          <Text strong>{title}</Text>
          {level && level !== "required" ? <ImportanceTag level={level} /> : null}
        </Space>
        <br />
        <Text type="secondary" style={{fontSize: 12}}>
          {status}
        </Text>
      </div>
      <Text type="secondary">→</Text>
    </div>
  );
}

/** Stubbed: clicking "set up" just marks the item done, so the states are visible. */
export function applyCustomisation(
  current: Customisation,
  key: keyof Customisation
): Customisation {
  if (key === "checkoutQuestions") {
    return {...current, checkoutQuestions: current.checkoutQuestions ? 0 : 3};
  }
  if (key === "coverImage") {
    return {...current, coverImage: !current.coverImage};
  }
  return {...current, automations: !current.automations};
}
